import React from 'react';
import { 
  Card, Typography, Alert, Space, Select, Row, Col, 
  Empty, Paragraph 
} from 'antd';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';

const { Option } = Select;
const { Text, Paragraph: AntParagraph } = Typography;

export const StopTimeAnalysis = ({ 
  reportData,
  selectedRoute,
  selectedTrip,
  setSelectedRoute,
  setSelectedTrip,
  dateRange
}) => {
  // Get unique routes for filter dropdown
  const routes = [...new Set(reportData.parsedData.map(item => item.routeNo))].sort();

  // Process data for the selected trip
  const processTripData = () => {
    if (!selectedRoute || !selectedTrip || selectedRoute === 'all' || selectedTrip === 'all') {
      return null;
    }

    // Get all stops for this trip within the date range
    const tripStops = reportData.parsedData
      .filter(item => 
        item.routeNo === selectedRoute &&
        item.tripId === selectedTrip &&
        item.tripDate >= dateRange[0].format('YYYY-MM-DD') &&
        item.tripDate <= dateRange[1].format('YYYY-MM-DD')
      )
      .sort((a, b) => {
        // Sort by date and scheduled time
        const dateCompare = a.tripDate.localeCompare(b.tripDate);
        if (dateCompare !== 0) return dateCompare;
        
        const [dateA, timeA] = a.scheduledTime.split(' ');
        const [dateB, timeB] = b.scheduledTime.split(' ');
        const [hoursA, minutesA] = timeA.split(':');
        const [hoursB, minutesB] = timeB.split(':');
        return (parseInt(hoursA) * 60 + parseInt(minutesA)) - (parseInt(hoursB) * 60 + parseInt(minutesB));
      });

    // Group data by stop
    const stopGroups = tripStops.reduce((acc, stop) => {
      if (!acc[stop.stopId]) {
        acc[stop.stopId] = {
          stopId: stop.stopId,
          stopName: stop.stopName,
          scheduledTimes: [],
          actualTimes: [],
          dates: []
        };
      }
      
      const [date, time] = stop.scheduledTime.split(' ');
      const [hours, minutes] = time.split(':');
      const scheduledTimeValue = (parseInt(hours) * 60 + parseInt(minutes)) * 60 * 1000; // Convert to milliseconds
      
      const [actualDate, actualTime] = (stop.actualTime || stop.scheduledTime).split(' ');
      const [actualHours, actualMinutes] = actualTime.split(':');
      const actualTimeValue = (parseInt(actualHours) * 60 + parseInt(actualMinutes)) * 60 * 1000; // Convert to milliseconds
      
      acc[stop.stopId].scheduledTimes.push(scheduledTimeValue);
      acc[stop.stopId].actualTimes.push(actualTimeValue);
      acc[stop.stopId].dates.push(stop.tripDate);
      
      return acc;
    }, {});

    // Convert to array format for the chart
    return Object.values(stopGroups).map(stop => ({
      stopName: stop.stopName,
      stopId: stop.stopId,
      scheduledTime: stop.scheduledTimes[0], // Use first occurrence for baseline
      actualTimes: stop.actualTimes,
      dates: stop.dates,
      variance: stop.actualTimes.map((actual, index) => 
        (actual - stop.scheduledTimes[index]) / (60 * 1000) // Convert to minutes
      )
    }));
  };

  const chartData = processTripData();

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Select 
              value={selectedRoute} 
              onChange={value => { setSelectedRoute(value); setSelectedTrip('all'); }}
              style={{ width: '100%' }}
              placeholder="Select route"
            >
              <Option value="all">All Routes</Option>
              {routes.map(route => (
                <Option key={route} value={route}>Route {route}</Option>
              ))}
            </Select>
          </Col>
          <Col span={12}>
            {selectedRoute !== 'all' && (() => {
              const tripIds = [...new Set(
                reportData.parsedData
                  .filter(item => item.routeNo === selectedRoute)
                  .map(item => item.tripId)
              )].sort();
              
              return (
                <Select
                  value={selectedTrip}
                  onChange={setSelectedTrip}
                  style={{ width: '100%' }}
                  placeholder="Select trip"
                >
                  <Option value="all">All Trips</Option>
                  {tripIds.map(tripId => (
                    <Option key={tripId} value={tripId}>
                      Trip {tripId}
                    </Option>
                  ))}
                </Select>
              );
            })()}
          </Col>
        </Row>
      </div>
      
      {selectedRoute === 'all' || selectedTrip === 'all' ? (
        <Alert
          message="Select a Route and Trip"
          description={
            <AntParagraph>
              Please select a specific route and trip from the filters above to view the stop time analysis.
              This will show you the actual arrival/departure times compared to the scheduled times for each stop.
            </AntParagraph>
          }
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      ) : !chartData || chartData.length === 0 ? (
        <Empty description="No data available for the selected route and trip" />
      ) : (
        <Card>
          <div style={{ height: 800 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 50, bottom: 100 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="stopName"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                />
                <YAxis
                  tickFormatter={value => {
                    const hours = Math.floor(value / (60 * 60 * 1000));
                    const minutes = Math.floor((value % (60 * 60 * 1000)) / (60 * 1000));
                    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                  }}
                  domain={[
                    dataMin => dataMin - 1000,
                    dataMax => dataMax + 1000
                  ]}
                  scale="time"
                  type="number"
                />
                <RechartsTooltip
                  formatter={value => {
                    const hours = Math.floor(value / (60 * 60 * 1000));
                    const minutes = Math.floor((value % (60 * 60 * 1000)) / (60 * 1000));
                    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                  }}
                  labelFormatter={label => `Stop: ${label}`}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="scheduledTime"
                  name="Scheduled Time"
                  stroke="#8884d8"
                  dot={true}
                />
                {chartData[0].actualTimes.map((_, index) => (
                  <Line
                    key={`actual-${index}`}
                    type="monotone"
                    dataKey={`actualTimes[${index}]`}
                    name={`Actual Time (${chartData[0].dates[index]})`}
                    stroke={['#82ca9d', '#ffc658', '#ff8042', '#0088fe'][index % 4]}
                    dot={true}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          
          <div style={{ marginTop: 16 }}>
            <Alert
              message="Stop Time Analysis"
              description={
                <AntParagraph>
                  This chart shows the scheduled times (blue line) compared to actual arrival/departure times (colored lines) for each stop.
                  Each colored line represents a different day in the selected date range.
                  The vertical distance between the scheduled and actual times indicates the variance in service performance.
                </AntParagraph>
              }
              type="info"
              showIcon
            />
          </div>
        </Card>
      )}
    </>
  );
};

export default StopTimeAnalysis; 